import { datadogRum } from '@datadog/browser-rum';
import { useCallback } from 'react';

type UseInterviewCompletionParams = {
  setIsCompleting: (isCompleting: boolean) => void;
  stopRecording: () => Promise<void>;
  finishUserCameraUpload?: () => Promise<void> | undefined;
  stopScreenRecording?: () => Promise<void>;
  finishScreenRecordingUpload?: () => Promise<void> | undefined;
  completeInterview: () => Promise<void> | void;
};

export const useInterviewCompletion = ({
  setIsCompleting,
  stopRecording,
  finishUserCameraUpload,
  stopScreenRecording,
  finishScreenRecordingUpload,
  completeInterview,
}: UseInterviewCompletionParams) => {
  const cleanupAndCompleteInterview = useCallback(async () => {
    setIsCompleting(true);
    console.log('Stopping recording');
    datadogRum.addAction('stopping_video_recording');

    const stopAndUpload = async (
      stopFn: () => Promise<void>,
      finishUploadFn: (() => Promise<void> | undefined) | undefined,
      label: string,
    ) => {
      try {
        await stopFn();
      } catch (error) {
        console.error(`Failed to stop ${label} recording`, error);
        datadogRum.addError(error);
      }

      console.log(`Finishing ${label} upload`);
      datadogRum.addAction(`finishing_${label}_upload`);

      try {
        await finishUploadFn?.();
      } catch (error) {
        console.error(`Failed to finish ${label} upload`, error);
        datadogRum.addError(error);
      }
    };

    // Stop and upload all recordings in parallel
    const uploadPromises = [
      stopAndUpload(stopRecording, finishUserCameraUpload, 'user_camera'),
    ];

    if (stopScreenRecording) {
      uploadPromises.push(
        stopAndUpload(
          stopScreenRecording,
          finishScreenRecordingUpload,
          'screen_recording',
        ),
      );
    }

    await Promise.all(uploadPromises);

    // Complete interview only once after all uploads are done
    console.log('Completing interview');
    datadogRum.addAction('completing_interview');

    try {
      await completeInterview();
    } catch (error) {
      console.error('Failed to complete interview', error);
      datadogRum.addError(error);
    }

    console.log('Interview complete');
  }, [
    setIsCompleting,
    stopRecording,
    finishUserCameraUpload,
    stopScreenRecording,
    finishScreenRecordingUpload,
    completeInterview,
  ]);

  const forceCompleteInterview = useCallback(async () => {
    datadogRum.addAction('stopping_video_recording');

    // Stop all recordings
    try {
      await stopRecording();
    } catch (error) {
      console.error('Failed to stop video recording', error);
      datadogRum.addError(error);
    }

    if (stopScreenRecording) {
      try {
        await stopScreenRecording();
      } catch (error) {
        console.error('Failed to stop screen recording', error);
        datadogRum.addError(error);
      }
    }

    // Finish uploads
    datadogRum.addAction('finishing_video_upload');

    try {
      await finishUserCameraUpload?.();
    } catch (error) {
      console.error('Failed to finish user camera upload', error);
      datadogRum.addError(error);
    }

    if (finishScreenRecordingUpload) {
      try {
        await finishScreenRecordingUpload();
      } catch (error) {
        console.error('Failed to finish screen recording upload', error);
        datadogRum.addError(error);
      }
    }

    console.log('Completing interview');
    datadogRum.addAction('completing_interview');

    try {
      await completeInterview();
    } catch (error) {
      console.error('Failed to complete interview', error);
      datadogRum.addError(error);
    }

    console.log('Interview complete');
  }, [
    stopRecording,
    stopScreenRecording,
    finishUserCameraUpload,
    finishScreenRecordingUpload,
    completeInterview,
  ]);

  return { cleanupAndCompleteInterview, forceCompleteInterview };
};
